// @flow
import type { BasketItem, TestSchedule, Product } from "../../lib/Types";
import BasketData from "../../lib/BasketData";

import React, { Fragment } from "react";
import styled, { css } from "styled-components";
import { DefaultPaddedContainer } from "./Theme";
import { ShoppingCart } from "styled-icons/fa-solid/ShoppingCart";
import NumberPicker from "./NumberPicker";
import Expander from "../Expander";
import Button from "../Button";
import ClientSuiteModal from "../products/modals/ClientSuiteModal";
import ClientSpecificationModal from "../products/modals/ClientSpecificationModal";
import Link from "../Link";
import AddToShoppingList from "./AddToShoppingList";

const Container = styled(DefaultPaddedContainer)`
  padding: 0px;
  margin-bottom: 20px;
  transition: border 300ms ease;
  border: 2px solid white;
  ${props =>
    props.highlight &&
    css`
      border: 2px solid #26b5d5;
    `}
  ${props =>
    props.active &&
    css`
      border: 1px solid #4cc3e8;
    `};
  & :hover {
    ${props =>
      !props.expanded &&
      css`
        background-color: #fafafa;
      `};
  }
`;
const ItemsContainer = styled.div`
  padding: 35px;
  padding-top: 10px;
`;
const Header = styled.div`
  cursor: pointer;
  padding: 15px;
  border-radius: 10px;
  user-select: none;
`;
const Table = styled.table`
  width: 100%;
`;
const Tr = styled.tr`
  > td,
  th {
    padding: 5px;
    border-bottom: 1px dotted #ddd;
  }
`;
const SampleCount = styled.span`
  font-size: 12pt;
  font-weight: bold;
  margin-left: 20px;
  color: #dddddd;
  ${props =>
    props.count > 0 &&
    css`
      color: #656565;
    `};
`;

type Props = {
  basket: BasketData,
  startRegistering: Function,
  company_id: number,
  onShoppingListChange: Function
};

type State = {
  expanded: boolean,
  showClientSuite: ?number,
  showClientSpecification: ?number,
  addingToShoppingList: boolean,
  highlight: boolean
};

export default class Basket extends React.Component<Props, State> {
  state = {
    expanded: false,
    showClientSuite: null,
    showClientSpecification: null,
    addingToShoppingList: false,
    highlight: false
  };

  handleBasketChange = () => {
    this.forceUpdate.bind(this);
    this.setState({ highlight: true }, () => {
      const basketItems = this.props.basket.basketItems();
      if (basketItems.length == 0) {
        this.setState({ expanded: false });
      }
      setTimeout(() => {
        this.setState({ highlight: false });
      }, 310);
    });
  };

  // Force a re-render of everything when the basket changes
  componentDidMount() {
    this.props.basket.subscribe(this.handleBasketChange);
  }

  componentWillUnmount() {
    this.props.basket.unsubscribe(this.handleBasketChange);
  }

  showSuite = (client_suite_id: number) => this.setState({ showClientSuite: client_suite_id });
  showSpec = (client_specification_id: number) =>
    this.setState({ showClientSpecification: client_specification_id });
  hideSuite = () => this.setState({ showClientSuite: null, showClientSpecification: null });
  toggleExpand = () => this.setState({ expanded: !this.state.expanded });
  updateBasket = (basketItem: BasketItem) => this.props.basket.addSchedule(basketItem);
  emptyBasket = () => this.props.basket.clearBasket();

  renderContents() {
    const { basket, startRegistering, registeringBranch } = this.props;
    const { expanded, showClientSuite, showClientSpecification } = this.state;
    const basketItems = basket.basketItems();
    const sampleCount = basket.sampleCount();
    if (!expanded) return null;
    if (sampleCount == 0) {
      return (
        <ItemsContainer>
          <p>{I18n.t("your_basket_is_currently_empty")}</p>
        </ItemsContainer>
      );
    }
    return (
      <ItemsContainer>
        {showClientSuite && <ClientSuiteModal id={showClientSuite} onClose={this.hideSuite} />}
        {showClientSpecification && (
          <ClientSpecificationModal id={showClientSpecification} onClose={this.hideSuite} />
        )}
        <Table>
          <thead>
            <Tr>
              <th style={{ width: 100 }}>Count</th>
              <th>{I18n.t("product")}</th>
              <th>{I18n.t("suite")}</th>
              <th>{I18n.t("specification")}</th>
            </Tr>
          </thead>
          <tbody>
            {basketItems.map(({ product, test_schedule, count }, i) => (
              <Tr key={i}>
                <td>
                  <NumberPicker
                    value={count}
                    onChange={(newCount: number) =>
                      this.updateBasket({
                        product,
                        test_schedule,
                        count: newCount
                      })
                    }
                  />
                </td>
                <td>{product.name}</td>
                <td>
                  <Link onClick={() => this.showSuite(test_schedule.client_suite_id)}>
                    {test_schedule.client_suite_name}
                  </Link>
                </td>
                <td>
                  <Link onClick={() => this.showSpec(test_schedule.client_specification_id)}>
                    {test_schedule.client_specification_name}
                  </Link>
                </td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Button
          primary
          disabled={basketItems.length == 0 || !registeringBranch}
          title={
            I18n.t("begin_registration_at") +
            " " +
            (registeringBranch ? registeringBranch.name : I18n.t("no_branch_selected"))
          }
          onClick={startRegistering}
          style={{ marginTop: 20 }}
        />
        <div className="pull-right">
          <Button
            disabled={basketItems.length == 0 || !registeringBranch}
            title={I18n.t("add_to_list")}
            onClick={() => this.setState({ addingToShoppingList: true })}
            style={{ marginTop: 20 }}
            icon="fa-plus"
          />
          {basketItems.length > 0 && (
            <Button
              disabled={basketItems.length == 0}
              title={I18n.t("empty_basket")}
              onClick={this.emptyBasket}
              style={{ marginLeft: 20, marginTop: 20 }}
            />
          )}
        </div>
      </ItemsContainer>
    );
  }

  render() {
    const { basket, company_id, onShoppingListChange } = this.props;
    const {
      expanded,
      showClientSuite,
      showClientSpecification,
      addingToShoppingList,
      highlight
    } = this.state;
    const basketItems = basket.basketItems();

    return (
      <Fragment>
        {addingToShoppingList && (
          <AddToShoppingList
            basket={basket}
            company_id={company_id}
            onClose={() => this.setState({ addingToShoppingList: false })}
            onChange={onShoppingListChange}
          />
        )}
        <Container expanded={expanded} highlight={highlight}>
          <Header onClick={this.toggleExpand}>
            <ShoppingCart size={15} style={{ marginRight: 10 }} /> {I18n.t("basket")}
            <SampleCount count={basket.sampleCount()}>
              <Expander expanded={expanded} size={15} />
              {basket.sampleCount()} {I18n.t("samples")} / {basket.uniqueScheduleCount()}{" "}
              {I18n.t("schedules")}
            </SampleCount>
          </Header>
          {this.renderContents()}
        </Container>
      </Fragment>
    );
  }
}
